import styles from "./styles/audio-file-input.module.scss";
import { audio } from "@/data/extensions";
import Image from "next/image";
import deleteIcon from "@/assets/convert/delete.svg";
import defaultIcon from "@/assets/icons/default-media-icon.webp";
import uploadIcon from "@/assets/convert/upload.svg";
import React from "react";
import { useMobile } from "@/hooks/use-responsive";
import { LicenseName } from "@/data/license.data";
import { useGlobalModalContext } from "@/providers/modal-provider/modal-provider";
import PlayerButton from "@/components/common/player-button";
import WavePlayerProgress from "@/components/common/music-player/wave-player-progress";
import { useWaveSurfer } from "@/hooks/music-players/use-wave-surfer";
import MediaFormatsInputPlayer from "@/components/common/music-player/media-formats-input-player";
import { useLocalFileTracking } from "@/hooks/mixpanel/media-formats/local-file-tracker";

interface AudioFileInputProps {
  isLimited: boolean;
  license: LicenseName;
  fileUrl: string;
  isDragging: boolean;
  localFile: File | null;
  localFileName: string;
  handleFileChange: (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => Promise<void>;
  handleDrop: (event: React.DragEvent<HTMLDivElement>) => Promise<void>;
  handleDropCancel: (event: any) => void;
  handleDeleteButtonClick: () => void;
  handleDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  handleDragEnter: (event: React.DragEvent<HTMLDivElement>) => void;
  handleDragLeave: (event: React.DragEvent<HTMLDivElement>) => void;
}

const AudioFileInput = ({
  isLimited,
  license,
  fileUrl,
  isDragging,
  localFile,
  localFileName,
  handleFileChange,
  handleDrop,
  handleDropCancel,
  handleDeleteButtonClick,
  handleDragOver,
  handleDragEnter,
  handleDragLeave,
}: AudioFileInputProps) => {
  const { isMobile } = useMobile();
  const { showModal } = useGlobalModalContext();
  const { trackLocalFile } = useLocalFileTracking();

  const handleWithLicense = async (
    event: any,
    action: (event: any) => Promise<void>,
    cancelAction?: (event: any) => void,
  ) => {
    if (isLimited) {
      showModal({
        type: "license_limited_modal",
        props: {
          licenseName: license,
          allowsMessage: ``,
          upgradeToMessage: "upload audio",
        },
      });
      if (cancelAction) {
        cancelAction(event);
      }
      return;
    }
    await action(event);
  };

  const handleDropWithLicense = async (event: any) => {
    return await handleWithLicense(
      event,
      () => handleDrop(event),
      () => handleDropCancel(event),
    );
  };

  const handleFileChangeWithLicense = async (event: any) => {
    const inputElement = event.target;
    await handleWithLicense(event, async () => {
      await handleFileChange(event);
      if (localFile) {
        trackLocalFile(localFileName);
      }
    });
    inputElement.value = "";
  };

  const input = (
    <input
      type="file"
      id="fileInput"
      className={styles.input}
      onChange={handleFileChangeWithLicense}
      accept={audio}
    />
  );

  return (
    <div
      className={
        isDragging
          ? styles.input_wrapper_dragging
          : styles.input_wrapper_default
      }
      onDragOver={handleDragOver}
      onDrop={handleDropWithLicense}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
    >
      {localFile ? (
        <div className={styles.result_wrapper}>
          <label>{input}</label>
          <div className={styles.bin} onClick={handleDeleteButtonClick}>
            <Image className={styles.bin_icon} src={deleteIcon} alt="delete" />
          </div>

          <MediaFormatsInputPlayer url={fileUrl} fileName={localFileName} />
        </div>
      ) : (
        <label className={styles.input_container}>
          {input}
          <Image className={styles.upload_icon} src={uploadIcon} alt="Upload" />
          <div className={styles.text_wrapper}>
            <div className={styles.upload_text}>Upload Audio</div>
            <div className={styles.drag_drop_text}>
              {/*{isDragging*/}
              {/*  ? "Move your file here"*/}
              {/*  : isMobile*/}
              {/*    ? "Supported .mp3, .wav, .flac, .m4r, .m4a, .ogg, .aac, .opus files"*/}
              {/*    : "Or Drag And Drop Mp3/ Wav/ FLAC/ M4R/ M4A/ Ogg/ AAC/ Opus File Here"}*/}
              Supported .mp3, .wav, .flac, .m4r, .m4a, .ogg, .aac, .opus files
            </div>
          </div>
        </label>
      )}
    </div>
  );
};

export default AudioFileInput;
